import React from 'react'
import { Link, VisuallyHidden } from '@overdose/components'
import { IconArrowRight } from '@tabler/icons-react'
import classNames from 'classnames'

import { Image } from '~/components/Image'
import { getImageSrc } from '~/helpers'
import { useAlgoliaSearchInsignts } from '~/hooks'
import Typography, { TypographyTag, TypographyVariant } from '../Typography'
import styles from './CategoryCard.module.css'
import { CategoryCardProps } from './CategoryCard.types'

export const CategoryCard = ({
  image,
  title,
  action,
  backgroundColor,
  cardTitleColor,
  isDesktopCarouselDisabled,
  categorySku,
  enableOverlayLink = false,
  width,
  showButton,
}: CategoryCardProps) => {
  const { sendViewProductsEvent } = useAlgoliaSearchInsignts()

  const renderOverlayLink = () => {
    if (!action?.url) {
      return null
    }

    return (
      <Link
        className='absolute z-10 inset-0'
        to={action.url}
        onClick={() => {
          sendViewProductsEvent([
            {
              title,
              url: action.url,
              listId: categorySku,
            },
          ])
        }}
      />
    )
  }

  return (
    <div
      className={classNames(
        'p-4 md:p-6 relative rounded-md overflow-hidden',
        styles.card,
        {
          [styles.desktopCarouselDisabledCard]: isDesktopCarouselDisabled,
          [styles.superNarrowCard]: width === 'super-narrow',
          'bg-white': !backgroundColor,
        }
      )}
      style={{ backgroundColor: backgroundColor || 'none' }}>
      {enableOverlayLink && renderOverlayLink()}
      {image && image?.src?.length > 0 && (
        <Image
          title={title}
          src={getImageSrc(image.src, '350')}
          addSrcSet={false}
          alt={image.altText}
          width={image.width}
          height={image.height}
          className={classNames(
            styles.image,
            'object-cover absolute inset-0 h-full w-full'
          )}
        />
      )}
      <Typography
        tag={TypographyTag.p}
        variant={TypographyVariant.Heading6}
        className={styles.title}
        style={
          {
            '--color-typography-heading': cardTitleColor,
          } as React.CSSProperties
        }>
        {title}
      </Typography>
      {action && !action.url.startsWith('/url/') && showButton && (
        <div
          className={classNames(
            'absolute lg:bottom-6 bottom-4',
            'bg-white rounded',
            'py-2 px-3 lg:py-3.5 lg:px-4',
            styles.actionContainer,
            {
              'lg:right-6 right-4': action.buttonPosition === 'right',
            }
          )}>
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegularBold}
            className='text-brand-primary'>
            {action.linkText}
          </Typography>
          <IconArrowRight
            size={20}
            color='var(--color-brand-primary)'
            strokeWidth={2}
            className='inline-block ml-2'
          />
          <VisuallyHidden>Browse {title}</VisuallyHidden>
        </div>
      )}
    </div>
  )
}
